import { Id } from '@/infra/id'
import { collection } from '@/infra/collection'
import { type IamApi } from '@/features/iam/api/iam-api'
import { TermsOfUseDocument } from './TermsOfUseDocument'
import { mapTermsOfUseResourceToProps } from './terms-of-use-mappers'

/**
 * Manages terms of use versions and acceptances.
 */
export class TermsOfUseStore {
  /**
   * Terms of use documents.
   */
  readonly termsOfUseDocuments = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new TermsOfUseDocument(),
  })

  constructor(private readonly _api: IamApi) {}

  /**
   * Fetches the latest terms for the platform, or for the workspace provided.
   */
  async fetchLatestTermsOfUseDocument(
    workspaceId: Id | null,
    signal?: AbortSignal,
  ): Promise<TermsOfUseDocument> {
    const resource = workspaceId
      ? await this._api.termsOfUse.getLatestForWorkspace(
          Id.assert(workspaceId),
          signal,
        )
      : await this._api.termsOfUse.getLatestForPlatform(signal)
    return this.termsOfUseDocuments.set(mapTermsOfUseResourceToProps(resource))
  }

  /**
   * Accepts the latest terms for the platform, or for the workspace provided.
   */
  async acceptLatestTerms(workspaceId: Id | null): Promise<void> {
    if (workspaceId) {
      await this._api.termsOfUse.acceptLatestForWorkspace(
        Id.assert(workspaceId),
      )
    } else {
      await this._api.termsOfUse.acceptLatestForPlatform()
    }
  }

  /**
   * Fetches whether the latest terms of use have been accepted.
   */
  async fetchTermsOfUseAcceptance(
    workspaceId: Id | null,
    signal?: AbortSignal,
  ): Promise<boolean> {
    const acceptance = workspaceId
      ? await this._api.termsOfUse.checkAcceptanceForWorkspace(
          Id.assert(workspaceId),
          signal,
        )
      : await this._api.termsOfUse.checkAcceptanceForPlatform(signal)
    return acceptance.accepted
  }
}
